import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { RevealHeading, Reveal } from "@/components/Reveal";
import { LogoLoader } from "@/components/LogoLoader";
import { DrawReveal } from "@/components/DrawReveal";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/b08e7dc5-f5ef-47e4-ba74-cce130d25cfc/images/ed7590a9e7b6d22125d735521d26e54d9a8a7b4b0d0ec6ce7e9837cb6109e447.jpeg";

const chapters = [
  {
    n: "01",
    title: "Precision Tuning",
    body: "Custom ECU calibration on the dyno, flex-fuel conversions, and transmission logic. We extract every reliable horsepower your platform can hold.",
    img: "https://static.prod-images.emergentagent.com/jobs/b08e7dc5-f5ef-47e4-ba74-cce130d25cfc/images/3c30861f679e439abfc3d6b99f87c0250ec9f2e82dce36c55fbded433cad8813.jpeg",
    to: "/tuning",
    cta: "Request Tuning",
  },
  {
    n: "02",
    title: "Custom Fabrication",
    body: "Hand-built exhausts, intercooler piping, roll cages and one-off brackets. TIG-welded, jig-checked, and built to survive the track.",
    img: "https://static.prod-images.emergentagent.com/jobs/b08e7dc5-f5ef-47e4-ba74-cce130d25cfc/images/32b16f074211996c011883e07dc597292aaf2ac29677bc50511c501dcc33bee7.jpeg",
    to: "/fabrication",
    cta: "Request Fabrication",
  },
  {
    n: "03",
    title: "The ViceTuned Badge",
    body: "Every car leaves running at its absolute best. Real support, honest recommendations, and a customer experience the performance world rarely offers.",
    img: "https://static.prod-images.emergentagent.com/jobs/b08e7dc5-f5ef-47e4-ba74-cce130d25cfc/images/843352f37f1b537b662faea7d42a9f11c329f779b8241b8424a2949441aeeeeb.jpeg",
    to: "/vehicles",
    cta: "See Supported Cars",
  },
];

export default function Landing() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.35, 0.8]);

  return (
    <div className="bg-void min-h-screen" data-testid="landing-page">
      <LogoLoader bg={HERO_IMG} />
      <Navbar />

      {/* HERO */}
      <section ref={heroRef} className="relative h-screen overflow-hidden flex items-end">
        <motion.div style={{ y, scale }} className="absolute inset-0">
          <img src={HERO_IMG} alt="Performance build" className="w-full h-full object-cover" />
        </motion.div>
        <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-void" />
        <div className="absolute inset-0 bg-gradient-to-t from-void via-void/20 to-transparent" />

        <div className="relative z-10 w-full mx-auto max-w-[1600px] px-5 md:px-10 pb-16 md:pb-24">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-xs uppercase tracking-[0.3em] text-neon mb-6 flex items-center gap-3"
          >
            <span className="w-8 h-px bg-neon" /> Vice Performance Engineering
          </motion.div>
          <h1 className="font-display uppercase text-[16vw] md:text-[13vw] lg:text-[11vw] leading-[0.82] tracking-tight text-[#FAFAFA]">
            <RevealHeading lines={["Built To", "Dominate."]} delay={0.35} />
          </h1>
          <div className="mt-8 flex flex-col md:flex-row md:items-end justify-between gap-8">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.8 }}
              className="font-mono text-sm md:text-base text-[#9CA3AF] max-w-md leading-relaxed"
            >
              Uncompromising performance, reliability, and quality. Modern performance, built.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.15, duration: 0.8 }}
              className="flex flex-wrap gap-3"
            >
              <Link
                to="/build"
                data-testid="hero-tuning-btn"
                className="group bg-neon text-void font-bold uppercase tracking-[0.15em] text-xs py-4 px-8 flex items-center gap-2 hover:bg-[#FAFAFA] transition-colors"
              >
                Request A Build <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </Link>
              <Link
                to="/vehicles"
                data-testid="hero-vehicles-btn"
                className="border border-[#333] text-[#FAFAFA] uppercase tracking-[0.15em] text-xs py-4 px-8 flex items-center gap-2 hover:border-neon hover:text-neon transition-colors"
              >
                Supported Vehicles
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-[#222] bg-void py-6">
        <Marquee speed={30} gradient={false}>
          {Array.from({ length: 8 }).map((_, i) => (
            <span key={i} className="font-display text-stroke text-6xl md:text-8xl uppercase mx-8">
              {"#VICETUNED\u00A0\u00A0\u00A0#VICEBUILT"}
            </span>
          ))}
        </Marquee>
      </div>

      {/* STATS STRIP */}
      <section className="border-b border-[#222] grid grid-cols-2 md:grid-cols-4">
        {[
          ["300+", "Cars Tuned"],
          ["15", "Years of Tuning"],
          ["200+", "Brands"],
          ["9–5", "Central Time · Mon–Fri"],
        ].map(([big, small], i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div className={`p-8 md:p-12 border-[#222] ${i < 3 ? "border-r" : ""} ${i < 2 ? "border-b md:border-b-0" : ""}`}>
              <div className="font-display text-5xl md:text-6xl text-neon leading-none">{big}</div>
              <div className="mt-3 text-xs uppercase tracking-[0.2em] text-[#9CA3AF]">{small}</div>
            </div>
          </Reveal>
        ))}
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section className="mx-auto max-w-[1600px] px-5 md:px-10 py-24 md:py-32">
        <Reveal>
          <div className="text-xs uppercase tracking-[0.3em] text-neon mb-4">// What We Do</div>
          <h2 className="font-display uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.85] tracking-tight max-w-4xl">
            Three ways we <span className="text-stroke">unleash</span> your machine.
          </h2>
        </Reveal>

        <div className="mt-20 flex flex-col">
          {chapters.map((c, i) => (
            <Reveal key={c.n} delay={0.05}>
              <Link
                to={c.to}
                data-testid={`chapter-${c.n}`}
                className="group grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-12 border-t border-[#222] items-center"
              >
                <div className="md:col-span-1 font-display text-4xl md:text-5xl text-[#333] group-hover:text-neon transition-colors">
                  {c.n}
                </div>
                <div className="relative md:col-span-4 overflow-hidden aspect-[4/3] md:aspect-[16/10]">
                  <DrawReveal
                    src={c.img}
                    alt={c.title}
                    imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="md:col-span-5">
                  <h3 className="font-display uppercase text-3xl md:text-5xl leading-none tracking-tight group-hover:translate-x-2 transition-transform">
                    {c.title}
                  </h3>
                  <p className="mt-4 font-mono text-sm md:text-base text-[#9CA3AF] max-w-md leading-relaxed">{c.body}</p>
                </div>
                <div className="md:col-span-2 flex md:justify-end">
                  <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#FAFAFA] group-hover:text-neon transition-colors">
                    {c.cta} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#222] bg-[#0A0010]">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 py-24 md:py-40 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-[500px] h-[500px] bg-accent-purple opacity-40 blur-[120px] rounded-full pointer-events-none" />
          <Reveal>
            <h2 className="font-display uppercase text-6xl md:text-8xl lg:text-[9vw] leading-[0.82] tracking-tight relative z-10">
              Ready to run<br /><span className="text-neon">ViceTuned?</span>
            </h2>
            <p className="mt-8 font-mono text-[#9CA3AF] max-w-lg relative z-10">
              Tell us about your car and your goals. We&apos;ll build a plan that delivers real, reliable power.
            </p>
            <div className="mt-10 flex flex-wrap gap-3 relative z-10">
              <Link to="/build" data-testid="cta-build" className="bg-neon text-void font-bold uppercase tracking-[0.15em] text-xs py-4 px-8 hover:bg-[#FAFAFA] transition-colors">
                Start A Build Request
              </Link>
              <Link to="/tuning" data-testid="cta-tuning" className="border border-[#333] uppercase tracking-[0.15em] text-xs py-4 px-8 hover:border-neon hover:text-neon transition-colors">
                Start A Tuning Request
              </Link>
              <Link to="/fabrication" data-testid="cta-fabrication" className="border border-[#333] uppercase tracking-[0.15em] text-xs py-4 px-8 hover:border-neon hover:text-neon transition-colors">
                Start A Fabrication Request
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
